import type { Locale } from "@tutor/protocol";
import { UiProvider } from "@tutor/ui";
import { createContext, createElement, useCallback, useContext, type ReactNode } from "react";

// Interface strings for the learner app. Item content and tutor replies arrive already
// in the learner's locale; only the app chrome is translated here. Placeholders are
// written `{name}` and filled by `t(key, { name })`. These are child screens: the app is
// "Coach" here, never "Tutor" (docs/copy/README.md, "Naming").

const en = {
  "app.title": "Coach",
  "app.loading": "Getting things ready…",

  "connection.offline": "You're offline. Your answers are still checked right here.",
  "connection.queued": "{count} waiting to send",
  "connection.queuedOne": "1 waiting to send",
  "connection.reconnecting": "Reconnecting…",
  "connection.unauthorized": "Type your PIN again to keep going.",

  "lesson.startPractice": "Let's practise",
  "lesson.startPracticeHint": "Ready when you are. Tap for your first exercise.",
  "lesson.nextPracticeHint": "Shall we keep going? Your next exercise is waiting.",
  "lesson.retryPractice": "Try again",
  "class.ask": "What are you doing in class right now?",
  "class.now": "In class right now",
  "class.none": "Nothing in particular",
  "class.test": "Test on {date}: we'll go over it before then.",
  "class.subject.math": "Maths",
  "class.subject.fra": "French",
  "class.subject.sci": "Science and technology",
  "class.subject.svt": "Life and earth sciences",
  "class.subject.pc": "Physics and chemistry",
  "class.subject.hg": "History and geography",
  "class.subject.emc": "Civics",
  "class.subject.tech": "Technology",
  "practice.blocked.paused":
    "Coach will be back tomorrow. Until then, if you need to talk, a grown-up you trust is there for you, and {helpline} answers for free, day and night. A parent can also reopen the session.",
  "practice.blocked.dailyCap":
    "That's all your practice time for today. Well done! See you tomorrow.",
  "practice.blocked.quietHours": "It's rest time now. Practice opens again a bit later.",
  "practice.blocked.noSubject":
    "No exercise is set for you right now. Ask a parent to choose your subjects.",
  "practice.blocked.askParent":
    "Let's take a break. Tell your parents how it's going, and come back when you're ready.",
  "practice.blocked.other": "No exercise just now. Try again in a little while.",
  "lesson.preparing": "Getting your exercise ready…",
  "lesson.openTutor": "Ask Coach",
  "lesson.closeTutor": "Close",
  "lesson.newMessage": "New message from Coach",
  "lesson.reviewTitle": "Quick review",
  "lesson.peekInvite": "Stuck? Coach is right here. Tap to write.",

  "help.label": "Ask Coach for help",
  "workbench.label": "Coach's workbench",
  "board.today": "Today",
  "board.label": "Coach's board",
  "board.goal.climb": "Climb: {skill}",
  "board.goal.review": "Review: {skill}",
  "board.goal.state.done": "done",
  "board.goal.state.current": "you're on it",
  "board.goal.state.todo": "still to do",
  "board.goal.state.setAside": "for next time",
  "board.goalCount": "goal {n} of {total}",
  "board.pages": "Today's pages",
  "board.page": "Page {n}",
  "board.thisExercise": "My exercise",
  "board.try": "Try {n}: {answer}",
  "board.tryRight": "right",
  "board.tryNotYet": "not yet",
  "board.backToExercise": "Back to my exercise",
  "board.moment.discover": "I discover · Coach shows",
  "board.moment.try": "I try",
  "board.moment.practice": "I practise",
  "board.moment.found": "Found it",
  "board.tryN": "Try {n}:",
  "board.work.title": "My working",
  "board.work.add": "+ write a line",
  "board.work.new": "A new line of working",
  "board.work.line": "Line {n}:",
  "board.work.full": "Your sheet is full. Tell Coach what you found.",
  "board.lessonCheck.title": "Quick check",
  "board.lessonCheck.answer": "Quick check answer",
  "board.lessonCheck.send": "Send",
  "board.lessonCheck.again": "Not quite. Have another look and try again.",
  "board.lessonCheck.skip": "Go to the exercise",
  "board.lessonPage.title": "Coach's lesson",
  "board.lessonPage.example": "Example",
  "board.lessonPage.trap": "Watch out:",
  "board.lessonPage.again": "See the lesson again",
  "board.lessonTeaching.title": "Coach explains first",
  "board.lessonTeaching.note": "Read what Coach writes. Your exercise comes right after.",
  "board.teachBack.title": "Explain in your own words",
  "board.teachBack.ask": "Why is your answer right? One or two sentences is enough.",
  "board.teachBack.placeholder": "Because…",
  "board.teachBack.send": "Send",
  "board.teachBack.skip": "Or move on: it's up to you.",
  "board.teachBack.sent": "Reading your explanation…",
  "board.teachBack.counted": "Well explained! It counts on your progress.",
  "board.teachBack.kept": "Thanks, it's on your page.",
  "board.work.mark": "Look at this line again",
  "board.steps.label": "Coach's steps",
  "board.steps.next": "The next step comes once you've answered.",
  "opener.kicker": "Let's pick up",
  "opener.lastTime": "Last time",
  "opener.lastSkill": "You worked on {skill}.",
  "opener.firstTry": "{n} of {total} right first time.",
  "opener.question": "What shall we do today?",
  "opener.today": "Today",
  "opener.climb": "Move up",
  "opener.review": "Review first",
  "opener.climbWhy": "The next step. I show you, then you try.",
  "opener.reviewWhy": "A warm-up to keep what you already know.",
  "opener.go": "Let's go",
  "opener.typeHint": "Or tell Coach what you want to work on, like “a test on fractions”.",
  "fin.open": "I'm done for today",
  "fin.kicker": "That's it for today",
  "fin.title": "Today's progress",
  "fin.newStep": "New step",
  "fin.plan": "Today's plan",
  "fin.kept": "{skill}, kept for next time",
  "fin.next": "Next time:",
  "fin.count": "Exercises finished: {n} · right first time: {m}",
  "fin.bye.step": "You climbed a step today. Well done, see you next time!",
  "fin.bye.plan": "The whole plan is done. See you next time!",
  "fin.bye.other": "Thanks for this session. See you next time!",
  "fin.mood": "Today was…",
  "fin.mood.good": "fine",
  "fin.mood.meh": "so-so",
  "fin.mood.hard": "hard",
  "fin.moodHint": "One tap, or skip it.",
  "fin.close": "See you next time",
  "fin.back": "I'll keep going",
  "fin.closed": "See you next time!",
  "fin.again": "Start a new session",
  "help.rephrase": "I don't get the question",
  "help.clue": "A clue",
  "help.method": "How do I go about it?",
  "help.example": "Show me an example",

  "item.yourAnswer": "Your answer",
  "item.check": "Check",
  "item.next": "Next",
  "item.waitingNext": "Good work. Your next exercise is on its way.",
  "item.numberHint": "Type a number like 3/4, 0.75 or 2.",
  "item.textPlaceholder": "Type your answer",
  "item.correct": "That's right. You worked it out yourself.",
  "item.correctMore": "There it is. You kept at it and got there.",
  "item.wrong": "Not yet. Look at your steps once more and try again.",
  "item.wrongMore":
    "Still not there, and that's useful: you've ruled something out. Try a smaller step.",
  "item.checking": "Got it. Checking your answer…",
  "item.recheckedCorrect": "Checked again: your answer is right.",
  "item.recheckedWrong": "Checked again: not yet this time. Have another go.",
  "item.attempts": "Tries: {count}",
  "item.outOfTries":
    "That's the end of this exercise: you've used all your tries. Move on to the next one.",
  "item.waitingNextAfterTries": "Your next exercise is on its way.",
  "item.rightAnswer": "The right answer: {answer}",
  "item.hintLabel": "A hint:",

  "chat.label": "Conversation with Coach",
  "curiosity.ideas": "Ideas to start",
  "curiosity.example1": "Why is the sky blue?",
  "curiosity.example2": "How does a volcano work?",
  "curiosity.example3": "Why do cats purr?",
  "chat.placeholder": "Ask a question or explain your idea…",
  "chat.send": "Send",
  "chat.you": "You",
  "chat.tutor": "Coach",
  "chat.sending": "Sending…",
  "chat.writing": "Coach is writing…",
  "chat.empty": "Say hello. Ask Coach about your exercise, or about anything you're curious about.",
  "chat.failed.unavailable": "Coach is taking a little break. Try again in a moment.",
  "chat.failed.blocked":
    "Coach will be back tomorrow. Until then, if you need to talk, a grown-up you trust is there for you, and {helpline} answers for free, day and night.",
  "chat.failed.budget": "That's all the chatting for today. You can still keep practising.",
  "chat.disclosure":
    "Coach is a computer program, so check its ideas: it can make mistakes. Never share your name, school or address.",

  "intro.title": "Meet Coach",
  "intro.body":
    "Coach is a computer program that uses AI to write its messages, not a person. It won't give you answers straight away, on purpose: finding them yourself is how they stick. It can get things wrong, so check its ideas. Never type your name, school or address. Your parent can see your progress, and if something you write worries us about your safety, your parent will be told.",
  "intro.ok": "Got it",

  "streak.first": "First day done. That's how a habit starts.",
  "streak.days": "{count} days in a row. Regularity is the whole trick.",
  "streak.back": "Good to see you. Let's pick up where you left off.",
  "mastery.new": "{skill} is yours now. You built it one step at a time.",
  "mastery.review": "A quick look back at {skill} is coming up, so it stays sharp.",
  "session.done": "Good session. You worked properly, and that's what counts.",

  "widget.numberLine": "Number line from {min} to {max}",
  "widget.numberLinePoint": "Your point is at {value}",
  "widget.fractionBar": "{shaded} of {parts} parts shaded",
  "widget.choiceSent": "Sent!",

  "settings.display": "Reading and display",

  "insights.title": "Your progress",
  "insights.empty": "Your progress will show up here after a few exercises.",
  "insights.summits": "Your summits",
  "insights.new": "New stage",
  "insights.started": "Started",

  "progress.steps": "{skill}: step {step} of {steps}, {stage}",
  "progress.stepOf": "Step {step} of {steps}",
  "progress.stage.0": "Starting out",
  "progress.stage.1": "First steps",
  "progress.stage.2": "On the way",
  "progress.stage.3": "Taking shape",
  "progress.stage.4": "Nearly there",
  "progress.stage.5": "At the summit",
  "progress.next": "Next: {stage}",
  "progress.start": "A few right answers and you're on the first step.",
  "progress.summit": "You've got it. We'll come back to it now and then so it stays.",
  "moment.step.title": "Step {step}: {stage}!",
  "moment.step.body": "{skill}: you climbed it by practising. Keep going.",
  "moment.harder.title": "You took on a harder one",
  "moment.harder.body": "{skill}: you're ready for tougher questions.",
  "moment.streak.title": "{count} in a row",
  "moment.streak.body": "Steady work like this is what makes it stick.",
  "moment.review.title": "Still sharp",
  "moment.review.body": "{skill} stayed with you. That's real learning.",
  "voice.label": "Talking with Coach",
  "voice.open": "Talk to Coach",
  "voice.resume": "Keep talking",
  "voice.holdToTalk": "Hold to talk",
  "voice.listening": "I'm listening. Let go when you're done",
  "voice.thinking": "Thinking…",
  "voice.speaking": "Hold to talk",
  "voice.connecting": "Getting ready…",
  "voice.reconnecting": "Just a moment…",
  "voice.paused": "Talking is paused",
  "voice.stop": "Stop talking",
  "voice.retry": "Try again",
  "voice.backToTyping": "Back to typing",
  "voice.minutesLeft": "{minutes} minutes of talking left",
  "voice.minuteLeft": "1 minute of talking left",
  "voice.usedUpNow": "Voice time is used up for now. You can keep going by typing!",
  "voice.usedUpToday": "Voice time is used up for today. You can keep going by typing!",
  "voice.usedUpMonth": "Voice time is used up for this month. You can keep going by typing!",
  "voice.notEnabled": "Talking isn't switched on yet. You can keep going by typing!",
  "voice.unavailable": "Talking isn't working right now. You can keep going by typing.",
  "voice.stopped": "Talking stopped. You can keep typing, or try again.",
  "voice.micDenied": "To talk, let this app use the microphone. You can keep typing too.",
} as const;

export type MessageKey = keyof typeof en;
export type Dictionary = Readonly<Record<MessageKey, string>>;

const fr: Dictionary = {
  "app.title": "Coach",
  "app.loading": "Un instant, on prépare tout…",

  "connection.offline":
    "Pas de connexion internet. Tes réponses sont quand même vérifiées sur cet appareil.",
  "connection.queued": "{count} en attente d'envoi",
  "connection.queuedOne": "1 en attente d'envoi",
  "connection.reconnecting": "Reconnexion…",
  "connection.unauthorized": "Retape ton code pour continuer.",

  "lesson.startPractice": "On s'entraîne",
  "lesson.startPracticeHint": "Quand tu veux ! Appuie pour lancer ton premier exercice.",
  "lesson.nextPracticeHint": "On continue ? Ton prochain exercice t'attend.",
  "lesson.retryPractice": "Réessayer",
  "class.ask": "Qu'est-ce que tu fais en classe en ce moment ?",
  "class.now": "En classe en ce moment",
  "class.none": "Rien de particulier",
  "class.test": "Contrôle le {date} : on révise d'ici là.",
  "class.subject.math": "Mathématiques",
  "class.subject.fra": "Français",
  "class.subject.sci": "Sciences et technologie",
  "class.subject.svt": "SVT",
  "class.subject.pc": "Physique-chimie",
  "class.subject.hg": "Histoire-géographie",
  "class.subject.emc": "EMC",
  "class.subject.tech": "Technologie",
  "practice.blocked.paused":
    "Coach revient demain. D'ici là, si tu as besoin de parler, un adulte de confiance est là pour toi, et le {helpline} répond gratuitement, jour et nuit. Un parent peut aussi rouvrir la séance.",
  "practice.blocked.dailyCap": "C'est tout pour l'entraînement aujourd'hui. Bravo ! À demain.",
  "practice.blocked.quietHours":
    "C'est l'heure de se reposer. L'entraînement reprend un peu plus tard.",
  "practice.blocked.noSubject":
    "Aucun exercice n'est prévu pour toi en ce moment. Demande à un parent de choisir tes matières.",
  "practice.blocked.askParent":
    "On fait une pause. Raconte à tes parents comment ça se passe, et reviens quand tu veux.",
  "practice.blocked.other": "Pas d'exercice pour l'instant. Réessaie dans un petit moment.",
  "lesson.preparing": "On prépare ton exercice…",
  "lesson.openTutor": "Demander à Coach",
  "lesson.closeTutor": "Fermer",
  "lesson.newMessage": "Nouveau message de Coach",
  "lesson.reviewTitle": "Petite révision",
  "lesson.peekInvite": "Tu bloques ? Coach est là. Appuie ici pour lui écrire.",

  "help.label": "Demander de l'aide à Coach",
  "workbench.label": "Le plan de travail de Coach",
  "board.today": "Aujourd'hui",
  "board.label": "Le tableau de Coach",
  "board.goal.climb": "Progresser : {skill}",
  "board.goal.review": "Réviser : {skill}",
  "board.goal.state.done": "fait",
  "board.goal.state.current": "en cours",
  "board.goal.state.todo": "à faire",
  "board.goal.state.setAside": "pour la prochaine fois",
  "board.goalCount": "but {n} sur {total}",
  "board.pages": "Les pages d'aujourd'hui",
  "board.page": "Page {n}",
  "board.thisExercise": "Mon exercice",
  "board.try": "Essai {n} : {answer}",
  "board.tryRight": "juste",
  "board.tryNotYet": "pas encore",
  "board.backToExercise": "Retour à mon exercice",
  "board.moment.discover": "Je découvre · Coach montre",
  "board.moment.try": "J'essaie",
  "board.moment.practice": "Je m'entraîne",
  "board.moment.found": "Trouvé",
  "board.tryN": "Essai {n} :",
  "board.work.title": "Mon brouillon",
  "board.work.add": "+ écrire une ligne",
  "board.work.new": "Une nouvelle ligne de brouillon",
  "board.work.line": "Ligne {n} :",
  "board.work.full": "Ta feuille est pleine. Dis à Coach ce que tu as trouvé.",
  "board.lessonCheck.title": "Je vérifie",
  "board.lessonCheck.answer": "Réponse à « Je vérifie »",
  "board.lessonCheck.send": "Envoyer",
  "board.lessonCheck.again": "Pas tout à fait. Regarde encore et réessaie.",
  "board.lessonCheck.skip": "Passe à l'exercice",
  "board.lessonPage.title": "La leçon de Coach",
  "board.lessonPage.example": "Exemple",
  "board.lessonPage.trap": "Attention :",
  "board.lessonPage.again": "Revoir la leçon",
  "board.lessonTeaching.title": "Coach t'explique d'abord",
  "board.lessonTeaching.note": "Lis ce que Coach écrit. Ton exercice arrive juste après.",
  "board.teachBack.title": "Explique avec tes mots",
  "board.teachBack.ask": "Pourquoi ta réponse est-elle juste ? Une ou deux phrases suffisent.",
  "board.teachBack.placeholder": "Parce que…",
  "board.teachBack.send": "Envoyer",
  "board.teachBack.skip": "Ou passe à la suite : c'est toi qui choisis.",
  "board.teachBack.sent": "Je lis ton explication…",
  "board.teachBack.counted": "Bien expliqué ! Ça compte pour ta progression.",
  "board.teachBack.kept": "Merci, c'est noté sur ta page.",
  "board.work.mark": "Regarde encore cette ligne",
  "board.steps.label": "Les étapes de Coach",
  "board.steps.next": "La suite s'écrit quand tu as répondu.",
  "opener.kicker": "On reprend",
  "opener.lastTime": "La dernière fois",
  "opener.lastSkill": "Tu as travaillé\u00a0: {skill}.",
  "opener.firstTry": "{n} sur {total} du premier coup.",
  "opener.question": "On fait quoi aujourd'hui\u00a0?",
  "opener.today": "Aujourd'hui",
  "opener.climb": "Progresser",
  "opener.review": "Réviser d'abord",
  "opener.climbWhy": "L'étape suivante. Je te montre, puis tu essaies.",
  "opener.reviewWhy": "Un échauffement pour garder ce que tu sais déjà.",
  "opener.go": "C'est parti",
  "opener.typeHint":
    "Ou écris à Coach ce que tu veux travailler, par exemple « un contrôle sur les fractions ».",
  "fin.open": "Je m'arrête pour aujourd'hui",
  "fin.kicker": "C'est fini pour aujourd'hui",
  "fin.title": "Ta progression du jour",
  "fin.newStep": "Nouvelle étape",
  "fin.plan": "Le plan d'aujourd'hui",
  "fin.kept": "{skill}, gardé pour la prochaine fois",
  "fin.next": "La prochaine fois\u00a0:",
  "fin.count": "Exercices finis\u00a0: {n} · du premier coup\u00a0: {m}",
  "fin.bye.step": "Tu as passé une étape aujourd'hui. Bravo, et à la prochaine\u00a0!",
  "fin.bye.plan": "Tout le plan est fait. À la prochaine\u00a0!",
  "fin.bye.other": "Merci pour cette séance. À la prochaine\u00a0!",
  "fin.mood": "Aujourd'hui, c'était…",
  "fin.mood.good": "ça va",
  "fin.mood.meh": "bof",
  "fin.mood.hard": "difficile",
  "fin.moodHint": "Un seul appui, et tu peux passer.",
  "fin.close": "À la prochaine",
  "fin.back": "Je continue",
  "fin.closed": "À la prochaine\u00a0!",
  "fin.again": "Une nouvelle séance",
  "help.rephrase": "Je ne comprends pas la question",
  "help.clue": "Un indice",
  "help.method": "Comment je m'y prends ?",
  "help.example": "Montre-moi un exemple",

  "item.yourAnswer": "Ta réponse",
  "item.check": "Vérifier",
  "item.next": "Suivant",
  "item.waitingNext": "Bien joué. Ton prochain exercice arrive.",
  "item.numberHint": "Écris un nombre, par exemple 3/4, 0,75 ou 2.",
  "item.textPlaceholder": "Écris ta réponse",
  "item.correct": "C'est juste ! Tu as trouvé par toi-même.",
  "item.correctMore": "Et voilà ! Tu as persévéré, et tu as trouvé.",
  "item.wrong": "Pas tout à fait. Relis tes étapes et réessaie.",
  "item.wrongMore":
    "Toujours pas, mais tu as déjà écarté une piste. Vas-y plus doucement, une étape à la fois.",
  "item.checking": "Bien reçu. On vérifie ta réponse…",
  "item.recheckedCorrect": "On a revérifié : ta réponse est juste.",
  "item.recheckedWrong": "On a revérifié : ce n'est pas encore ça. Réessaie.",
  "item.attempts": "Essais : {count}",
  "item.outOfTries": "Cet exercice s'arrête là : tu as utilisé tous tes essais. Passe au suivant.",
  "item.waitingNextAfterTries": "Ton prochain exercice arrive.",
  "item.rightAnswer": "La bonne réponse : {answer}",
  "item.hintLabel": "Un indice :",

  "chat.label": "Conversation avec Coach",
  "curiosity.ideas": "Des idées pour commencer",
  "curiosity.example1": "Pourquoi le ciel est-il bleu ?",
  "curiosity.example2": "Comment fonctionne un volcan ?",
  "curiosity.example3": "Pourquoi les chats ronronnent-ils ?",
  "chat.placeholder": "Pose une question ou explique ton idée…",
  "chat.send": "Envoyer",
  "chat.you": "Toi",
  "chat.tutor": "Coach",
  "chat.sending": "Envoi…",
  "chat.writing": "Coach écrit…",
  "chat.empty":
    "Dis bonjour ! Pose à Coach tes questions sur ton exercice, ou sur tout ce qui t'intrigue.",
  "chat.failed.unavailable": "Coach fait une petite pause. Réessaie dans un moment.",
  "chat.failed.blocked":
    "Coach revient demain. D'ici là, si tu as besoin de parler, un adulte de confiance est là pour toi, et le {helpline} répond gratuitement, jour et nuit.",
  "chat.failed.budget": "Fini de discuter pour aujourd'hui. Tu peux continuer tes exercices.",
  "chat.disclosure":
    "Coach est un programme informatique, alors vérifie ses idées : il peut se tromper. Ne donne jamais ton nom, ton école ni ton adresse.",

  "intro.title": "Voici Coach",
  "intro.body":
    "Coach est un programme informatique qui utilise l'IA pour écrire ses messages, pas une personne. Il ne te donnera pas les réponses tout de suite, c'est voulu : c'est en les trouvant toi-même que tu les retiens. Il peut se tromper, alors vérifie ses idées. Ne tape jamais ton nom, ton école ni ton adresse. Tes parents peuvent voir ta progression, et si quelque chose que tu écris nous inquiète pour ta sécurité, ils seront prévenus.",
  "intro.ok": "Compris",

  "streak.first": "Premier jour réussi ! C'est comme ça qu'on prend une bonne habitude.",
  "streak.days": "{count} jours de suite. Le secret, c'est la régularité.",
  "streak.back": "Content de te revoir ! On reprend là où tu en étais.",
  "mastery.new": "{skill}, c'est acquis ! Ton travail, pas à pas, a payé.",
  "mastery.review": "Bientôt, une petite révision sur {skill}, pour ne pas oublier.",
  "session.done": "Bonne séance. Tu as bien travaillé, et c'est ça qui compte.",

  "widget.numberLine": "Droite graduée de {min} à {max}",
  "widget.numberLinePoint": "Ton point est sur {value}",
  "widget.fractionBar": "{shaded} parts coloriées sur {parts}",
  "widget.choiceSent": "Envoyé !",

  "settings.display": "Lecture et affichage",

  "insights.title": "Ta progression",
  "insights.empty": "Ta progression s'affichera ici après quelques exercices.",
  "insights.summits": "Tes sommets",
  "insights.new": "Nouvelle étape",
  "insights.started": "Commencé",

  "progress.steps": "{skill} : étape {step} sur {steps}, {stage}",
  "progress.stepOf": "Étape {step} sur {steps}",
  "progress.stage.0": "Au départ",
  "progress.stage.1": "Premiers pas",
  "progress.stage.2": "En route",
  "progress.stage.3": "Ça prend forme",
  "progress.stage.4": "Presque au sommet",
  "progress.stage.5": "Au sommet",
  "progress.next": "Prochaine étape : {stage}",
  "progress.start": "Quelques bonnes réponses et tu passes à la première étape.",
  "progress.summit": "C'est acquis. On y reviendra de temps en temps pour ne pas l'oublier.",
  "moment.step.title": "Étape {step} : {stage} !",
  "moment.step.body": "{skill} : ton entraînement paie. Continue comme ça !",
  "moment.harder.title": "Tu as réussi un exercice plus dur",
  "moment.harder.body": "{skill} : place à des exercices plus corsés.",
  "moment.streak.title": "{count} bonnes réponses d'affilée",
  "moment.streak.body": "C'est en t'entraînant régulièrement que ça rentre.",
  "moment.review.title": "Toujours bien en tête",
  "moment.review.body": "{skill} : tu n'as rien oublié. C'est ça, apprendre pour de vrai.",
  "voice.label": "Parler avec Coach",
  "voice.open": "Parler à Coach",
  "voice.resume": "Continuer à parler",
  "voice.holdToTalk": "Reste appuyé pour parler",
  "voice.listening": "Je t'écoute. Relâche quand tu as fini",
  "voice.thinking": "Je réfléchis…",
  "voice.speaking": "Reste appuyé pour parler",
  "voice.connecting": "Je me prépare…",
  "voice.reconnecting": "Un instant…",
  "voice.paused": "Le micro est en pause",
  "voice.stop": "Arrêter de parler",
  "voice.retry": "Réessayer",
  "voice.backToTyping": "Revenir à l'écrit",
  "voice.minutesLeft": "Il te reste {minutes} minutes pour parler",
  "voice.minuteLeft": "Il te reste 1 minute pour parler",
  "voice.usedUpNow": "Plus de temps pour parler pour l'instant. Tu peux continuer en écrivant !",
  "voice.usedUpToday": "Plus de temps pour parler aujourd'hui. Tu peux continuer en écrivant !",
  "voice.usedUpMonth": "Plus de temps pour parler ce mois-ci. Tu peux continuer en écrivant !",
  "voice.notEnabled":
    "Parler à voix haute n'est pas encore activé. Tu peux continuer en écrivant !",
  "voice.unavailable": "Impossible de parler pour le moment. Tu peux continuer en écrivant.",
  "voice.stopped": "La discussion à voix haute s'est arrêtée. Tu peux écrire, ou réessayer.",
  "voice.micDenied": "Pour parler, autorise cette appli à utiliser le micro. Tu peux aussi écrire.",
};

export const dictionaries: Readonly<Record<Locale, Dictionary>> = { en, fr };

export type Translate = (
  key: MessageKey,
  vars?: Readonly<Record<string, string | number>>,
) => string;

export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Readonly<Record<string, string | number>>,
): string {
  const template = dictionaries[locale][key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : formatValue(locale, value);
  });
}

function formatValue(locale: Locale, value: string | number): string {
  return typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : value;
}

const LocaleContext = createContext<Locale>("en");

/** Sets the locale for the app's strings and for `@tutor/ui` components and widgets. */
export function LocaleProvider(props: { locale: Locale; children: ReactNode }) {
  return createElement(
    LocaleContext.Provider,
    { value: props.locale },
    createElement(UiProvider, { locale: props.locale, children: props.children }),
  );
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

export function useT(): Translate {
  const locale = useLocale();
  return useCallback<Translate>((key, vars) => translate(locale, key, vars), [locale]);
}
